import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, MessageSquare, Send, Plus, CheckCheck, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function ChatPage() {
  const { user } = useAuth();
  const isSeller = user?.role === "supplier";
  const [params] = useSearchParams();
  const [threads, setThreads] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [picked, setPicked] = useState([]);
  const [composeBody, setComposeBody] = useState("");
  const bottomRef = useRef(null);

  const loadThreads = (keepActive = true) =>
    api.get("/chat/threads").then((r) => {
      setThreads(r.data.threads);
      if (!keepActive) return r.data.threads;
      return r.data.threads;
    }).catch(() => {});

  const openThread = (tid) => {
    setActiveId(tid);
    api.get(`/chat/threads/${tid}/messages`).then((r) => {
      setMessages(r.data.messages);
      api.post(`/chat/threads/${tid}/read`).then(() => loadThreads()).catch(() => {});
    }).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => {
    loadThreads().then((list) => {
      if (!list) return;
      const wantSeller = params.get("seller");
      if (wantSeller) {
        const t = list.find((x) => x.supplier_company_id === wantSeller);
        if (t) { openThread(t.id); return; }
        setPicked([wantSeller]);
        setComposeOpen(true);
      } else if (list.length) {
        openThread(list[0].id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      loadThreads();
      if (activeId) api.get(`/chat/threads/${activeId}/messages`).then((r) => setMessages(r.data.messages)).catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, [activeId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!body.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post(`/chat/threads/${activeId}/messages`, { body: body.trim() });
      setBody("");
      openThread(activeId);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const openCompose = () => {
    api.get("/suppliers").then((r) => {
      setSellers(r.data.suppliers.filter((s) => s.status === "active" && s.supplier_company_id));
      setComposeOpen(true);
    }).catch((e) => toast.error(formatApiError(e)));
  };

  const sendNew = async () => {
    if (!picked.length) return toast.error("Select at least one seller");
    if (!composeBody.trim()) return toast.error("Type a message");
    setSending(true);
    try {
      const r = await api.post("/chat/threads", { supplier_company_ids: picked, body: composeBody.trim() });
      toast.success(picked.length > 1
        ? `Message sent to ${r.data.threads.length} sellers — each in a separate private conversation`
        : "Message sent");
      setComposeOpen(false);
      setComposeBody("");
      setPicked([]);
      const list = await loadThreads();
      const t = list?.find((x) => x.id === r.data.threads[0].id);
      if (t) openThread(t.id);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const active = threads?.find((t) => t.id === activeId);
  const threadTitle = (t) => (isSeller ? t.org_name : t.supplier_name);

  if (!threads) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="chat-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Chat</h1>
          <p className="text-sm text-muted-foreground">
            {isSeller ? "Private one-to-one conversations with your buyer." : "Message one or multiple sellers — every seller gets a private, separate conversation."}
          </p>
        </div>
        {!isSeller && (
          <Button data-testid="chat-new-message-btn" className="bg-slate-900 hover:bg-slate-800" onClick={openCompose}>
            <Plus className="mr-1.5 h-4 w-4" /> New Message
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="overflow-hidden rounded-lg border bg-card" data-testid="chat-thread-list">
          <div className="border-b px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Conversations</div>
          <div className="max-h-[560px] divide-y overflow-y-auto">
            {threads.length === 0 && <p className="p-5 text-sm text-muted-foreground">No conversations yet.</p>}
            {threads.map((t) => (
              <button key={t.id} data-testid={`chat-thread-${t.id.slice(0, 8)}`} onClick={() => openThread(t.id)}
                className={`block w-full px-4 py-3 text-left transition-colors hover:bg-slate-50 ${activeId === t.id ? "bg-slate-100" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-semibold">{threadTitle(t)}</p>
                  {t.unread > 0 && <span data-testid={`chat-unread-${t.id.slice(0, 8)}`} className="rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{t.unread}</span>}
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{t.last_message || "No messages yet"}</p>
                <div className="mt-1 flex gap-1.5">
                  {t.enquiry_number && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">{t.enquiry_number}</span>}
                  {t.po_number && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">{t.po_number}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col overflow-hidden rounded-lg border bg-card lg:col-span-2" data-testid="chat-conversation">
          {active ? (
            <>
              <div className="border-b px-5 py-3">
                <p className="text-sm font-semibold" data-testid="chat-active-title">{threadTitle(active)}</p>
                <p className="text-xs text-muted-foreground">
                  {isSeller ? `Buyer: ${active.buyer_name}` : `Seller: ${active.supplier_name}`}
                  {active.enquiry_number ? ` · Event: ${active.enquiry_number}` : ""}
                  {active.po_number ? ` · PO: ${active.po_number}` : ""}
                </p>
              </div>
              <div className="max-h-[440px] flex-1 space-y-3 overflow-y-auto p-5" data-testid="chat-messages">
                {messages.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">No messages yet. Say hello.</p>}
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  const read = (m.read_by || []).length > 1;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id.slice(0, 8)}`}>
                      <div className={`max-w-[75%] rounded-lg px-3.5 py-2.5 ${mine ? "bg-slate-900 text-white" : "border bg-white"}`}>
                        {!mine && <p className="mb-0.5 text-[11px] font-semibold text-slate-500">{m.sender_name}</p>}
                        <p className="whitespace-pre-wrap text-sm">{m.body}</p>
                        <p className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${mine ? "text-slate-300" : "text-muted-foreground"}`}>
                          {fmtDateTime(m.created_at)}
                          {mine && (read ? <span className="flex items-center gap-0.5 text-blue-300"><CheckCheck className="h-3 w-3" />Read</span>
                            : <span className="flex items-center gap-0.5"><Check className="h-3 w-3" />Sent</span>)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <div className="flex items-center gap-2 border-t p-3">
                <input data-testid="chat-input" value={body} onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                  placeholder="Type message..." className="flex-1 rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                <Button data-testid="chat-send-btn" disabled={sending || !body.trim()} onClick={send} className="bg-slate-900 hover:bg-slate-800">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center py-24 text-center">
              <MessageSquare className="h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm text-muted-foreground">Select a conversation{!isSeller && " or start a new message"}.</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="chat-compose-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Message</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            Selecting multiple sellers sends the same message as separate private conversations — sellers never see each other.
          </p>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">To:</p>
          <div className="mt-1.5 max-h-56 space-y-1 overflow-y-auto rounded-md border p-2" data-testid="chat-seller-picker">
            {sellers.length === 0 && <p className="p-2 text-sm text-muted-foreground">No approved sellers available.</p>}
            {sellers.map((s) => (
              <label key={s.supplier_company_id} className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-slate-50">
                <Checkbox data-testid={`chat-pick-${s.name.replace(/\W+/g, "-").toLowerCase()}`}
                  checked={picked.includes(s.supplier_company_id)}
                  onCheckedChange={(v) => setPicked((p) => v ? [...p, s.supplier_company_id] : p.filter((x) => x !== s.supplier_company_id))} />
                <span className="text-sm">{s.name}</span>
              </label>
            ))}
          </div>
          <textarea data-testid="chat-compose-body" rows={3} value={composeBody} onChange={(e) => setComposeBody(e.target.value)}
            placeholder="Type message..." className="mt-3 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
          <Button data-testid="chat-compose-send" disabled={sending} onClick={sendNew} className="mt-3 w-full bg-slate-900 hover:bg-slate-800">
            {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
